import { useEffect, useState } from "react"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { supabase } from "./supabaseClient"
import { Session } from "@supabase/supabase-js"

// Pages
import Login from "./pages/Login"
import Dashboard from "./pages/Dashboard"
import TeamPicker from "./pages/TeamPicker"
import MyEntries from "./pages/MyEntries"
import Leaderboard from "./pages/Leaderboard"
import TeamSummary from "./pages/TeamSummary"
import LiveScores from "./pages/LiveScores"
import Results from "./pages/Results"
import ChampionGame from "./pages/ChampionGame"
import Admin from "./pages/Admin"
import PaymentManager from "./pages/admin/PaymentManager"
import PayoutConfig from "./pages/admin/PayoutConfig"
import YearSetup from "./pages/admin/YearSetup"
import BracketManager from "./pages/admin/BracketManager"
import ScoringConfig from "./pages/admin/ScoringConfig"
import SchoolManager from "./pages/admin/SchoolManager"

// Components
import Layout from "./components/Layout"

// Protected Route wrapper
function ProtectedRoute({ children, session }: { children: React.ReactNode; session: Session | null }) {
  if (!session) {
    return <Navigate to="/login" replace />
  }
  return <Layout>{children}</Layout>
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public route */}
        <Route 
          path="/login" 
          element={session ? <Navigate to="/dashboard" replace /> : <Login />} 
        />

        {/* Protected routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute session={session}>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/team-picker" 
          element={
            <ProtectedRoute session={session}>
              <TeamPicker />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/my-entries" 
          element={
            <ProtectedRoute session={session}>
              <MyEntries />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/leaderboard" 
          element={
            <ProtectedRoute session={session}>
              <Leaderboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/teams" 
          element={
            <ProtectedRoute session={session}>
              <TeamSummary />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/live" 
          element={
            <ProtectedRoute session={session}>
              <LiveScores />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/results" 
          element={
            <ProtectedRoute session={session}>
              <Results />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/champion" 
          element={
            <ProtectedRoute session={session}>
              <ChampionGame />
            </ProtectedRoute>
          } 
        />

        {/* Admin routes */}
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute session={session}>
              <Admin />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/payments" 
          element={
            <ProtectedRoute session={session}>
              <PaymentManager />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/payouts" 
          element={
            <ProtectedRoute session={session}>
              <PayoutConfig />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/years" 
          element={
            <ProtectedRoute session={session}>
              <YearSetup />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/bracket" 
          element={
            <ProtectedRoute session={session}>
              <BracketManager />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/scoring" 
          element={
            <ProtectedRoute session={session}>
              <ScoringConfig />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/schools" 
          element={
            <ProtectedRoute session={session}>
              <SchoolManager />
            </ProtectedRoute>
          } 
        />

        {/* Default redirect */}
        <Route 
          path="*" 
          element={<Navigate to={session ? "/dashboard" : "/login"} replace />} 
        />
      </Routes>
    </BrowserRouter>
  )
}
